import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Form, Formik, Field, FieldProps, FormikHelpers, FormikBag, FormikState } from 'formik';
import MutationWithProgress from 'react-apollo/MutationWithProgress';
import { MutationFunction } from '@apollo/client/react';

import Styled from 'styled';
import ImageView from 'lib/images/image-view';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import ClickAwayDetectableView from 'lib/ui/click-away-detectable-view';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import EmojiSelectorContainer from 'four-nets/ui-v2/emoji-selector/emoji-selector-container';
import { insertEmojiToText } from 'four-nets/ui-v2/emoji-selector/insert-emoji-to-text';
import ImageInput from 'four-nets/ui-v2/form/image-input/image-input';
import { mergeValidators, emptyValidator } from 'four-nets/ui-v2/form/validators';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import IconSmile from 'four-nets/icons/svg/icon_smile.svg';
import Spinner from 'four-nets/ui/spinner/spinner';
import Upload from 'four-nets/ui-v2/upload/upload';
import Avatar from 'four-nets/avatar/avatar';
import { ViewerFragment } from 'four-nets/user/viewer/viewer.graphql';
import { HideForUnpaidVendor } from 'four-nets/vendor/permissions';

import uploadPhotoFile, {
    UploadPhotofileMutation,
    UploadPhotofileMutationVariables,
} from './mutations/upload-photofile.graphql';
import Textarea from './textarea/textarea';
import { CreatePhotoFileFragment } from './create.graphql';
import defaultStyles from './create.scss';

interface Values {
    comment: string;
    image: CreatePhotoFileFragment | File | null;
}

export type Mention = (username: string) => void;

export interface Props {
    styles: typeof defaultStyles;
    id?: string;
    placeholder?: string;
    submit: (values: Values & { image: CreatePhotoFileFragment }) => Promise<any>;
    autoFocusInput?: boolean;
    mention?: (mention: Mention) => void;
}

interface State {
    focused: boolean;
    progress: number;
    emojiSelection: boolean;
}

function isUploaded(value: any): value is CreatePhotoFileFragment {
    return value instanceof Object && value.__typename === 'PhotoFileNode';
}

interface TextareaWithEmojiAndPhotosProps {
    viewer: ViewerFragment | null;
    resetForm: () => void;
    field: any;
    isValid: boolean;
    setTextarea: (ref: HTMLTextAreaElement) => void;
    onImageChange: any;
    onChange: any;
    onFocus: any;
    onBlur: any;
    message: any;
    values: any;
    progress: any;
    setFieldValue: any;
    placeholder: any;
    onEmojiButtonClick: any;
    styles: any;
    autoFocusInput?: boolean;
    isSubmitting: any;
}

interface TextareaWithEmojiAndPhotosState {
    inputKey: number;
}

@ClickAwayDetectableView({ width: '100%' })
class TextareaWithEmojiAndPhotos extends React.PureComponent<
    TextareaWithEmojiAndPhotosProps,
    TextareaWithEmojiAndPhotosState
> {
    static contextTypes = {
        authCheck: PropTypes.func,
    };

    state = {
        inputKey: 0,
    };

    onClickAway = (options: { event: MouseEvent }) => {
        if (!this.props.values.comment && !this.props.values.image) {
            this.props.resetForm();
        }
        this.props.onBlur();
    };

    render() {
        const {
            viewer,
            field,
            isValid,
            setTextarea,
            onImageChange,
            onChange,
            onFocus,
            message,
            values,
            progress,
            setFieldValue,
            placeholder,
            onEmojiButtonClick,
            styles,
            autoFocusInput,
            isSubmitting,
        } = this.props;

        return (
            <React.Fragment>
                <Textarea
                    style={{ textarea: styles.textarea }}
                    attachments={
                        values.image ? (
                            <Upload
                                progress={progress}
                                onClose={() => {
                                    if (values.image) {
                                        // abort(values.image);
                                    }

                                    setFieldValue('image', null, true);
                                }}
                            >
                                {isUploaded(values.image) ? (
                                    <ImageView
                                        src={((values.image as unknown) as CreatePhotoFileFragment).src}
                                        srcSet={((values.image as unknown) as CreatePhotoFileFragment).srcset || ''}
                                        aspectFill={true}
                                        sizes={{ xs: 200 }}
                                    />
                                ) : null}
                            </Upload>
                        ) : null
                    }
                    autoComplete='off'
                    name={field.name}
                    setTextarea={setTextarea}
                    onChange={change => {
                        field.onChange(change);
                        onChange();
                    }}
                    onFocus={onFocus}
                    autoFocus={autoFocusInput}
                    placeholder={placeholder || (message as string)}
                    value={field.value}
                    actions={
                        <div className={styles.actions}>
                            <button type='button' className={styles.emojiButton} onClick={onEmojiButtonClick}>
                                <SVGIcon style={{ image: styles.iconSmile }} src={IconSmile} />
                            </button>
                            <ImageInput
                                key={this.state.inputKey}
                                multiple={false}
                                onClick={event => {
                                    if (!viewer) {
                                        event.preventDefault();
                                        this.context.authCheck(() => Promise.resolve(), AuthOrigin.NewComment);
                                    }
                                }}
                                onChange={event => {
                                    onImageChange(event);
                                    // this will force remount of input element, we want this,
                                    // because onChange event is not fired when user selects the same file
                                    this.setState(prevState => ({
                                        inputKey: prevState.inputKey + 1,
                                    }));
                                }}
                            />
                            <button type='submit' disabled={!isValid || isSubmitting} className={styles.button}>
                                <FormattedMessage id='BUTTON_LABEL_ADD' />
                            </button>
                            {isSubmitting ? <Spinner mini={true} /> : null}
                        </div>
                    }
                />
            </React.Fragment>
        );
    }
}

class Create extends React.PureComponent<Props, State> {
    static contextTypes = {
        authCheck: PropTypes.func,
    };

    state = {
        focused: false,
        progress: 0,
        emojiSelection: false,
    };

    textarea: HTMLTextAreaElement | null = null;
    setFieldValue?: (name: 'image' | 'comment', value: any, shouldValidate?: boolean) => void;
    values!: Values;
    timer?: NodeJS.Timer;
    setProgress?: (progress: number) => void;
    upload?: MutationFunction<UploadPhotofileMutation, UploadPhotofileMutationVariables>;

    setTextarea = (ref: HTMLTextAreaElement) => {
        this.textarea = ref;
    };

    validate = (values: Values) => {
        const errors: { [k in keyof Values]?: Array<React.ReactElement<FormattedMessage>> | true | undefined } = {};
        const commentErrors = mergeValidators([emptyValidator])(values.comment);

        if (commentErrors && !values.image) {
            errors.comment = commentErrors;
        }
        if (values.image instanceof File) {
            errors.image = true;
        }
        return errors;
    };

    onProgress = (event: ProgressEvent) => {
        if (this.setProgress) {
            this.setProgress(event.loaded / event.total);
        }
    };

    onClickAway = () => {
        this.setState({ emojiSelection: false });
    };

    onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files.length && event.target.files[0];

        if (this.setFieldValue) {
            this.setFieldValue('image', file, true);
        }

        if (file) {
            this.setState(
                {
                    progress: 0,
                },
                () => {
                    this.setProgress = progress => {
                        if (file === this.values.image) {
                            this.setState({
                                progress,
                            });
                        }
                    };
                    if (this.upload) {
                        this.upload({ variables: { file } }).then((result: any) => {
                            const image = (result as any).data!.uploadPhotoFiles!.photoFiles![0]!;
                            if (this.setFieldValue) {
                                this.setFieldValue('image', image, true);
                            }
                        });
                    }
                }
            );
        }
    };

    onTextareaChange = () => {
        this.setState({ emojiSelection: false });
    };

    onTextareaFocus = () => {
        this.setState({ focused: true });
    };

    onTextareaBlur = () => {
        this.setState({ focused: false });
    };

    toggleEmojiSelector = (event: MouseEvent) => {
        event.stopPropagation();
        this.setState(prevState => ({
            emojiSelection: !prevState.emojiSelection,
        }));

        if (this.textarea) {
            this.textarea.focus();
        }
    };

    onEmojiSelect = (_: any, emoji: any) => {
        if (!this.textarea) {
            return;
        }
        const { selectionStart } = this.textarea;
        const [newValue, insertedLen] = insertEmojiToText(this.textarea.value, emoji, selectionStart);
        const newCursorPosition = selectionStart + (insertedLen as number);

        if (this.setFieldValue) {
            this.setFieldValue('comment', newValue, false);
        }
        this.textarea.focus();
        setTimeout(() => this.textarea!.setSelectionRange(newCursorPosition, newCursorPosition), 0); // textarea sets cursor at end when set without timeout
    };

    render() {
        const { placeholder, styles, autoFocusInput, submit } = this.props;
        const { focused } = this.state;

        return (
            <MutationWithProgress<UploadPhotofileMutation, UploadPhotofileMutationVariables>
                mutation={uploadPhotoFile}
                onProgress={this.onProgress}
            >
                {upload => {
                    this.upload = upload;
                    return (
                        <ViewerConsumer>
                            {({ viewer }) => (
                                <HideForUnpaidVendor>
                                    <div className={styles.container}>
                                        <div className={styles.wrapper}>
                                            <Avatar style={{ avatar: styles.avatar }} user={viewer} />
                                            <Formik
                                                initialValues={{ comment: '', image: null }}
                                                validate={this.validate}
                                                onSubmit={(values, actions) => {
                                                    return this.context
                                                        .authCheck(
                                                            () =>
                                                                submit(
                                                                    values as Values & {
                                                                        image: CreatePhotoFileFragment;
                                                                    }
                                                                ),
                                                            AuthOrigin.NewComment
                                                        )
                                                        .then(() => {
                                                            actions.resetForm();
                                                        })
                                                        .finally(() => {
                                                            actions.setSubmitting(false);
                                                        });
                                                }}
                                            >
                                                {({ values, resetForm, setFieldValue, isValid, isSubmitting }) => {
                                                    this.setFieldValue = setFieldValue;
                                                    this.values = values;
                                                    this.setFieldValue = setFieldValue;

                                                    if (this.props.mention) {
                                                        this.props.mention(username => {
                                                            const previous = values.comment ? `${values.comment} ` : '';
                                                            setFieldValue('comment', `${previous}@${username} `, true);

                                                            if (this.textarea) {
                                                                if (document.activeElement !== this.textarea) {
                                                                    this.textarea.focus();
                                                                }
                                                            }
                                                        });
                                                    }

                                                    return (
                                                        <React.Fragment>
                                                            <Form
                                                                className={classnames(styles.form, {
                                                                    [styles.isValid]: isValid,
                                                                    [styles.focused]: focused,
                                                                    [styles.hasImage]: values.image != null,
                                                                })}
                                                            >
                                                                <Field name='comment'>
                                                                    {({ field }: FieldProps<Values>) => (
                                                                        <FormattedMessage id='COMMENT_PLACEHOLDER'>
                                                                            {message => (
                                                                                <TextareaWithEmojiAndPhotos
                                                                                    viewer={viewer}
                                                                                    resetForm={resetForm}
                                                                                    field={field}
                                                                                    isValid={isValid}
                                                                                    setTextarea={this.setTextarea}
                                                                                    onImageChange={this.onImageChange}
                                                                                    onChange={this.onTextareaChange}
                                                                                    onFocus={this.onTextareaFocus}
                                                                                    onBlur={this.onTextareaBlur}
                                                                                    message={message}
                                                                                    values={values}
                                                                                    progress={this.state.progress}
                                                                                    setFieldValue={setFieldValue}
                                                                                    placeholder={placeholder}
                                                                                    onEmojiButtonClick={
                                                                                        this.toggleEmojiSelector
                                                                                    }
                                                                                    styles={styles}
                                                                                    autoFocusInput={autoFocusInput}
                                                                                    isSubmitting={isSubmitting}
                                                                                />
                                                                            )}
                                                                        </FormattedMessage>
                                                                    )}
                                                                </Field>
                                                            </Form>
                                                        </React.Fragment>
                                                    );
                                                }}
                                            </Formik>
                                        </div>
                                        {this.state.emojiSelection ? (
                                            <div className={styles.emojiWindowWrapper}>
                                                <EmojiSelectorContainer
                                                    style={{ emojiWindow: styles.emojiWindow }}
                                                    onClickAway={this.onClickAway}
                                                    onEmojiSelect={this.onEmojiSelect}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                </HideForUnpaidVendor>
                            )}
                        </ViewerConsumer>
                    );
                }}
            </MutationWithProgress>
        );
    }
}

export default Styled(defaultStyles)(Create);
